import React, { useEffect, useState } from 'react';
import {
  Container,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Avatar,
  useTheme,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  LinearProgress,
  Alert,
  Tooltip,
  Button,
  Skeleton,
  ToggleButtonGroup,
  ToggleButton,
  Tabs,
  Tab,
} from '@mui/material';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
  ArcElement,
  BarController,
  PieController,
} from 'chart.js';
import Layout from '../components/Layout';
import PeopleIcon from '@mui/icons-material/People';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import SchoolIcon from '@mui/icons-material/School';
import WarningIcon from '@mui/icons-material/Warning';
import HistoryIcon from '@mui/icons-material/History';
import ChatIcon from '@mui/icons-material/Chat';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import GridViewIcon from '@mui/icons-material/GridView';
import DownloadIcon from '@mui/icons-material/Download';
import useStore from '../store/useStore';
import PdfExportPanel from '../components/PdfExportPanel';
import ClassOverview from '../components/analytics/ClassOverview';
import CompareView from '../components/analytics/CompareView';
import RecentAssessments from '../components/analytics/RecentAssessments';
import { useSearchParams } from 'react-router-dom';

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend,
  ArcElement,
  BarController,
  PieController
);

// ─── Colour helpers ──────────────────────────────────────────────────────────
function masteryBg(pct) {
  if (pct === null || pct === undefined) return '#f9fafb';
  if (pct >= 80) return '#d1fae5';
  if (pct >= 60) return '#fef9c3';
  if (pct >= 40) return '#fed7aa';
  return '#fee2e2';
}
function masteryFg(pct) {
  if (pct === null || pct === undefined) return '#9ca3af';
  if (pct >= 80) return '#065f46';
  if (pct >= 60) return '#713f12';
  if (pct >= 40) return '#7c2d12';
  return '#991b1b';
}
function masteryChipColor(pct) {
  if (pct >= 80) return 'success';
  if (pct >= 60) return 'warning';
  return 'error';
}
function daysChipColor(days) {
  if (days === null) return 'error';
  if (days <= 7) return 'success';
  if (days <= 21) return 'warning';
  return 'error';
}

// ─── Section header ───────────────────────────────────────────────────────────
function SectionHeader({ icon, title, subtitle }) {
  return (
    <Box sx={{ mb: 3, mt: 6 }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}
      >
        {icon} {title}
      </Typography>
      {subtitle && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
          {subtitle}
        </Typography>
      )}
      <Divider sx={{ mt: 1.5 }} />
    </Box>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function AnalyticsPage() {
  const [data, setData] = useState(null);
  const [globalData, setGlobalData] = useState(null);
  // null = "All templates" view (sorted by totalMastered — fair cross-level)
  // string = specific template view (sorted by avgMastery% — fair within-level)
  const [activeTemplate, setActiveTemplate] = useState(null);
  // Tabs by key (deep links: /analytics?tab=recent | compare)
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTabState] = useState(
    () => searchParams.get('tab') || 'overview'
  );
  const setTab = (key) => {
    setTabState(key);
    setSearchParams(key === 'overview' ? {} : { tab: key }, { replace: true });
  };
  const [pdfPanelOpen, setPdfPanelOpen] = useState(false);
  // Students handed to the report dialog / the comparison from other tabs
  const [exportIds, setExportIds] = useState([]);
  const [compareSeed, setCompareSeed] = useState(null);
  const openExport = (ids) => {
    setExportIds(ids || []);
    setPdfPanelOpen(true);
  };
  const { userRole, language } = useStore();
  const theme = useTheme();
  const en = language === 'en';

  useEffect(() => {
    fetch(`/api/analytics/teacher?lang=${language}`, {
      credentials: 'include',
    })
      .then((r) => r.json())
      .then(setData);
    if (userRole === 'admin') {
      fetch(`/api/analytics/global?lang=${language}`, {
        credentials: 'include',
      })
        .then((r) => r.json())
        .then(setGlobalData);
    }
  }, [userRole, language]);

  const handleExportCSV = () => {
    const exportRows = activeTemplate
      ? data.studentsByTemplate?.[activeTemplate] || []
      : data.studentScores || [];
    if (!exportRows.length) return;
    const header = [
      'Name',
      'Template',
      'Total Mastered',
      'Total Assessed',
      'Mastery % (within template)',
      'Trend %',
      'Days Since Assessed',
      'Status',
    ];
    const csvRows = exportRows.map((s) => [
      `"${s.name}"`,
      `"${s.primaryTemplate}"`,
      s.totalMastered ?? '',
      s.totalAssessed ?? '',
      s.avgMastery ?? '',
      s.progressData?.change ?? '',
      s.daysSinceAssessed ?? '',
      s.daysSinceAssessed > 21
        ? 'Inactive'
        : s.avgMastery >= 70
          ? 'Good'
          : 'Needs Attention',
    ]);
    const csv = [header, ...csvRows].map((r) => r.join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analytics_${activeTemplate || 'all'}_${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  if (!data) {
    return (
      <Layout title={en ? 'Analytics' : 'Аналитика'}>
        <Container maxWidth="xl">
          <Grid container spacing={3} sx={{ mb: 3 }}>
            {[0, 1, 2, 3].map((i) => (
              <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
                <Skeleton
                  variant="rounded"
                  height={110}
                  sx={{ borderRadius: 4 }}
                />
              </Grid>
            ))}
          </Grid>
          <Skeleton
            variant="rounded"
            height={48}
            sx={{ mb: 3, borderRadius: 3 }}
          />
          <Skeleton
            variant="rounded"
            height={320}
            sx={{ mb: 3, borderRadius: 4 }}
          />
          <Skeleton
            variant="rounded"
            height={48}
            sx={{ mb: 3, borderRadius: 3 }}
          />
          <Skeleton variant="rounded" height={280} sx={{ borderRadius: 4 }} />
        </Container>
      </Layout>
    );
  }

  const attentionCount = data.studentsNeedingAttention?.length || 0;
  const templateNames = Object.keys(data.studentsByTemplate || {}).sort();

  // When a template is selected, stats reflect that template only
  const activeTemplateData = activeTemplate
    ? data.templateAverages?.[activeTemplate]
    : null;

  const displayStudents = activeTemplate
    ? data.studentsByTemplate?.[activeTemplate] || []
    : data.studentScores || [];

  const activeHeatmap = activeTemplate
    ? data.heatmapByTemplate?.[activeTemplate]
    : null;

  // ─── Stat cards ─────────────────────────────────────────────────────────────
  // Top row: always show absolute totals (fair across all levels)
  const statCards = activeTemplate
    ? [
        {
          label: en ? 'Students on Template' : 'Учеников на шаблоне',
          value: activeTemplateData?.studentCount ?? 0,
          icon: <PeopleIcon />,
          color: '#3b82f6',
        },
        {
          label: en ? 'Total Mastered' : 'Всего усвоено',
          value: activeTemplateData?.totalMastered ?? 0,
          icon: <CheckCircleOutlineIcon />,
          color: '#10b981',
        },
        {
          label: en
            ? 'Avg Mastery (within template)'
            : 'Ср. усвоение (в шаблоне)',
          value: `${activeTemplateData?.avgMastery ?? 0}%`,
          icon: <TrendingUpIcon />,
          color:
            (activeTemplateData?.avgMastery ?? 0) >= 70
              ? '#10b981'
              : (activeTemplateData?.avgMastery ?? 0) >= 50
                ? '#f59e0b'
                : '#ef4444',
        },
        {
          label: en ? 'Need Attention' : 'Нужна помощь',
          value:
            data.studentsNeedingAttention?.filter((s) =>
              displayStudents.some((d) => d.name === s.name)
            ).length ?? 0,
          icon: <WarningIcon />,
          color: attentionCount > 0 ? '#ef4444' : '#10b981',
        },
      ]
    : [
        {
          label: en ? 'Total Students' : 'Всего учеников',
          value: data.totalStudents,
          icon: <PeopleIcon />,
          color: '#3b82f6',
        },
        {
          label: en
            ? 'Total Mastered (all levels)'
            : 'Всего усвоено (все уровни)',
          value: data.classTotalMastered ?? 0,
          icon: <CheckCircleOutlineIcon />,
          color: '#10b981',
          tooltip: en
            ? 'Absolute count of mastered items across all students and templates — the only fair cross-level metric'
            : 'Абсолютное количество усвоенных элементов — единственный справедливый показатель между уровнями',
        },
        {
          label: en ? 'Students assessed' : 'Оценено учеников',
          value: `${data.assessedStudents ?? 0} / ${data.totalStudents}`,
          icon: <AssessmentIcon />,
          color: '#7c3aed',
          tooltip: en
            ? 'Students with at least one scored (non-hidden) assessment'
            : 'Ученики хотя бы с одной заполненной (не скрытой) оценкой',
        },
        {
          label: en ? 'Need Attention' : 'Нужна помощь',
          value: attentionCount,
          icon: <WarningIcon />,
          color: attentionCount > 0 ? '#ef4444' : '#10b981',
        },
      ];

  const tabs = [
    { key: 'overview', label: en ? 'Overview' : 'Обзор' },
    { key: 'recent', label: en ? 'Recently assessed' : 'Недавно оценённые' },
    { key: 'compare', label: en ? 'Compare' : 'Сравнение' },
    { key: 'students', label: en ? 'Students' : 'Ученики' },
    { key: 'themes', label: en ? 'Themes' : 'Темы' },
    { key: 'activity', label: en ? 'Activity' : 'Активность' },
  ];
  if (userRole === 'admin')
    tabs.push({ key: 'school', label: en ? 'School' : 'Школа' });
  const tabIndex = Math.max(
    0,
    tabs.findIndex((t) => t.key === tab)
  );

  return (
    <Layout title={en ? 'Analytics' : 'Аналитика'}>
      <Container maxWidth="xl">
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 5,
            bgcolor: 'background.default',
            borderBottom: '1px solid',
            borderColor: 'divider',
            mb: 3,
            mx: { xs: -2, sm: -3 },
            px: { xs: 2, sm: 3 },
          }}
        >
          <Tabs
            value={tabIndex}
            onChange={(e, v) => setTab(tabs[v].key)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{ minHeight: 48 }}
          >
            {tabs.map((t) => (
              <Tab
                key={t.key}
                label={t.label}
                sx={{ fontWeight: 700, minHeight: 48, textTransform: 'none' }}
              />
            ))}
          </Tabs>
        </Box>
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
          <Button
            variant="contained"
            size="small"
            startIcon={<DownloadIcon />}
            onClick={() => openExport([])}
          >
            {en ? 'Download reports' : 'Скачать отчёты'}
          </Button>
        </Box>
        {tab === 'overview' && (
          <>
            {/* ══ STAT CARDS ══════════════════════════════════════════════════════ */}
            <Grid container spacing={3} sx={{ mb: 2 }}>
              {statCards.map((s, i) => (
                <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
                  <Tooltip title={s.tooltip || ''} arrow>
                    <Card
                      sx={{
                        borderRadius: 4,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                        cursor: s.tooltip ? 'help' : 'default',
                      }}
                    >
                      <CardContent
                        sx={{ display: 'flex', alignItems: 'center', p: 3 }}
                      >
                        <Avatar
                          sx={{
                            bgcolor: s.color,
                            width: 56,
                            height: 56,
                            mr: 2,
                          }}
                        >
                          {s.icon}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            {s.label}
                          </Typography>
                          <Typography variant="h4" sx={{ fontWeight: 700 }}>
                            {s.value}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>
                  </Tooltip>
                </Grid>
              ))}
            </Grid>

            <ClassOverview
              data={data}
              en={en}
              activeTemplate={activeTemplate}
              onPickTemplate={(name) => {
                setActiveTemplate(name);
                if (name) setTab('students');
              }}
            />

            {/* ══ STUDENTS NEEDING ATTENTION ═══════════════════════════════════════ */}
            {attentionCount > 0 && (
              <>
                <SectionHeader
                  icon={<ErrorOutlineIcon color="error" />}
                  title={
                    en
                      ? 'Students Needing Attention'
                      : 'Ученики, требующие внимания'
                  }
                  subtitle={
                    en
                      ? 'Assessed students only. Flagged when: not reassessed for 30+ days, dropped on the same units, or far below the average of their level'
                      : 'Только оценённые ученики. Отмечены: нет переоценки 30+ дней, снижение по тем же темам или заметно ниже среднего по своему уровню'
                  }
                />
                <Grid container spacing={2}>
                  {data.studentsNeedingAttention.map((s, i) => (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={i}>
                      <Alert
                        severity="warning"
                        icon={<ErrorOutlineIcon />}
                        sx={{
                          borderRadius: 3,
                          border: '1px solid',
                          borderColor: 'warning.light',
                        }}
                      >
                        <Typography
                          variant="subtitle2"
                          sx={{ fontWeight: 700 }}
                        >
                          {s.name}
                        </Typography>
                        {s.reasons.map((r, ri) => (
                          <Typography key={ri} variant="body2">
                            • {r}
                          </Typography>
                        ))}
                      </Alert>
                    </Grid>
                  ))}
                </Grid>
              </>
            )}
          </>
        )}

        {tab === 'recent' && (
          <RecentAssessments
            en={en}
            onExport={openExport}
            onCompare={(ids, templateId) => {
              setCompareSeed({ studentIds: ids, templateId, at: Date.now() });
              setTab('compare');
            }}
          />
        )}
        {tab === 'compare' && (
          <CompareView en={en} seed={compareSeed} onExport={openExport} />
        )}
        {tab === 'students' && (
          <>
            {/* ══ STUDENT PERFORMANCE TABLE ════════════════════════════════════════ */}
            <SectionHeader
              icon={<AssessmentIcon color="primary" />}
              title={en ? 'Student Performance' : 'Успеваемость учеников'}
              subtitle={
                activeTemplate
                  ? en
                    ? `Showing ${activeTemplate} — ranked by mastery % within this template (apples-to-apples)`
                    : `Показан шаблон ${activeTemplate} — ранг по усвоению внутри шаблона`
                  : en
                    ? 'All templates — ranked by total mastered items (fair cross-level comparison)'
                    : 'Все шаблоны — ранг по количеству усвоенных элементов (справедливое сравнение)'
              }
            />

            {/* Template filter */}
            {templateNames.length > 1 && (
              <Box sx={{ mb: 2 }}>
                <Alert
                  severity="info"
                  variant="outlined"
                  sx={{ mb: 2, borderRadius: 3 }}
                >
                  <Typography variant="body2">
                    {en
                      ? 'Comparing students from different levels by mastery % is unfair — a 70% on Level 1 (50 items) ≠ 70% on Level 2 (200 items). Select a template for within-level comparison, or use "All" to rank by absolute mastered items count.'
                      : 'Сравнение учеников с разных уровней по % некорректно. Выберите шаблон для честного сравнения внутри уровня, или используйте "Все" для ранжирования по количеству усвоенных элементов.'}
                  </Typography>
                </Alert>
                <ToggleButtonGroup
                  value={activeTemplate}
                  exclusive
                  onChange={(_, val) => setActiveTemplate(val)}
                  size="small"
                  sx={{ flexWrap: 'wrap', gap: 0.5 }}
                >
                  <ToggleButton
                    value={null}
                    sx={{ borderRadius: '20px !important', px: 2 }}
                  >
                    <Box
                      sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                    >
                      <PeopleIcon fontSize="small" />
                      <Typography variant="caption" sx={{ fontWeight: 700 }}>
                        {en ? 'All (by total mastered)' : 'Все (по усвоенным)'}
                      </Typography>
                    </Box>
                  </ToggleButton>
                  {templateNames.map((tmpl) => {
                    const stats = data.templateAverages?.[tmpl];
                    return (
                      <ToggleButton
                        key={tmpl}
                        value={tmpl}
                        sx={{ borderRadius: '20px !important', px: 2 }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                          }}
                        >
                          <SchoolIcon fontSize="small" />
                          <Typography
                            variant="caption"
                            sx={{ fontWeight: 700 }}
                          >
                            {tmpl}
                          </Typography>
                          {stats && (
                            <Chip
                              label={`${stats.studentCount} · ${stats.avgMastery}%`}
                              size="small"
                              color={masteryChipColor(stats.avgMastery)}
                              sx={{ height: 18, fontSize: '0.65rem', ml: 0.5 }}
                            />
                          )}
                        </Box>
                      </ToggleButton>
                    );
                  })}
                </ToggleButtonGroup>
              </Box>
            )}

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={handleExportCSV}
                sx={{ borderRadius: 3 }}
              >
                {en ? 'Export CSV' : 'Экспорт CSV'}
              </Button>
            </Box>

            <TableContainer
              component={Paper}
              sx={{
                borderRadius: 4,
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                mb: 3,
              }}
            >
              <Table>
                <TableHead sx={{ bgcolor: 'grey.50' }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 700, width: 40 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      {en ? 'Student' : 'Ученик'}
                    </TableCell>
                    {!activeTemplate && (
                      <TableCell sx={{ fontWeight: 700 }}>
                        {en ? 'Template' : 'Шаблон'}
                      </TableCell>
                    )}
                    {/* Primary metric column changes based on view */}
                    <TableCell sx={{ fontWeight: 700, minWidth: 200 }}>
                      {activeTemplate
                        ? en
                          ? 'Mastery % (within template)'
                          : 'Усвоение % (в шаблоне)'
                        : en
                          ? 'Total Mastered'
                          : 'Усвоено всего'}
                    </TableCell>
                    {activeTemplate && (
                      <TableCell sx={{ fontWeight: 700, minWidth: 140 }}>
                        {en ? 'Mastered / Assessed' : 'Усвоено / Оценено'}
                      </TableCell>
                    )}
                    <TableCell sx={{ fontWeight: 700 }}>
                      {en ? 'Trend' : 'Тренд'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      {en ? 'Last Assessed' : 'Последняя оценка'}
                    </TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>
                      {en ? 'Status' : 'Статус'}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {displayStudents.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={8}
                        sx={{
                          textAlign: 'center',
                          py: 4,
                          color: 'text.secondary',
                        }}
                      >
                        {en ? 'No students yet' : 'Учеников пока нет'}
                      </TableCell>
                    </TableRow>
                  ) : (
                    displayStudents.map((student, idx) => {
                      const isAttention = data.studentsNeedingAttention?.some(
                        (s) => s.name === student.name
                      );
                      const isExcelling = activeTemplate
                        ? student.avgMastery >= 80
                        : student.totalMastered > 0 &&
                          student.totalAssessed > 0 &&
                          student.totalMastered / student.totalAssessed >= 0.8;
                      return (
                        <TableRow
                          key={idx}
                          hover
                          sx={{ bgcolor: isAttention ? '#fff7ed' : 'inherit' }}
                        >
                          <TableCell
                            sx={{ color: 'text.disabled', fontWeight: 700 }}
                          >
                            {idx + 1}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 700 }}>
                            {student.name}
                          </TableCell>

                          {/* Template badge — only shown in All view */}
                          {!activeTemplate && (
                            <TableCell>
                              <Chip
                                label={student.primaryTemplate}
                                size="small"
                                variant="outlined"
                                color="primary"
                                sx={{ fontWeight: 600 }}
                              />
                            </TableCell>
                          )}

                          {/* Primary metric: totalMastered (All) or mastery% (template) */}
                          <TableCell>
                            {activeTemplate && student.scored === false ? (
                              <Typography variant="body2" color="text.disabled">
                                {en ? 'not assessed yet' : 'ещё не оценён'}
                              </Typography>
                            ) : activeTemplate ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                <LinearProgress
                                  variant="determinate"
                                  value={student.avgMastery}
                                  color={masteryChipColor(student.avgMastery)}
                                  sx={{ flex: 1, height: 8, borderRadius: 4 }}
                                />
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 700, minWidth: 36 }}
                                >
                                  {student.avgMastery}%
                                </Typography>
                              </Box>
                            ) : (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 1,
                                }}
                              >
                                <Typography
                                  variant="h6"
                                  sx={{
                                    fontWeight: 800,
                                    color: '#10b981',
                                    minWidth: 40,
                                  }}
                                >
                                  {student.totalMastered}
                                </Typography>
                                <Box sx={{ flex: 1 }}>
                                  <LinearProgress
                                    variant="determinate"
                                    value={
                                      student.totalAssessed > 0
                                        ? (student.totalMastered /
                                            student.totalAssessed) *
                                          100
                                        : 0
                                    }
                                    color="success"
                                    sx={{ height: 6, borderRadius: 3 }}
                                  />
                                  <Typography
                                    variant="caption"
                                    color="text.disabled"
                                  >
                                    {en ? 'of' : 'из'} {student.totalAssessed}{' '}
                                    {en ? 'items' : 'эл.'}
                                  </Typography>
                                </Box>
                              </Box>
                            )}
                          </TableCell>

                          {/* Mastered/Developing counts — only in template view */}
                          {activeTemplate && (
                            <TableCell>
                              <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Chip
                                  label={`✓ ${student.totalMastered}`}
                                  size="small"
                                  sx={{
                                    bgcolor: '#d1fae5',
                                    color: '#065f46',
                                    fontWeight: 700,
                                    fontSize: '0.7rem',
                                  }}
                                />
                                <Chip
                                  label={`~ ${student.totalDeveloping ?? 0}`}
                                  size="small"
                                  sx={{
                                    bgcolor: '#fef9c3',
                                    color: '#713f12',
                                    fontWeight: 700,
                                    fontSize: '0.7rem',
                                  }}
                                />
                              </Box>
                            </TableCell>
                          )}

                          {/* Trend */}
                          <TableCell>
                            {student.progressData ? (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                }}
                              >
                                {student.progressData.change > 0 ? (
                                  <TrendingUpIcon
                                    fontSize="small"
                                    color="success"
                                  />
                                ) : student.progressData.change < 0 ? (
                                  <TrendingDownIcon
                                    fontSize="small"
                                    color="error"
                                  />
                                ) : null}
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700,
                                    color:
                                      student.progressData.change > 0
                                        ? 'success.main'
                                        : student.progressData.change < 0
                                          ? 'error.main'
                                          : 'text.secondary',
                                  }}
                                >
                                  {student.progressData.change > 0 ? '+' : ''}
                                  {student.progressData.change}{' '}
                                  {en ? 'pp' : 'п.п.'}
                                </Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" color="text.disabled">
                                —
                              </Typography>
                            )}
                          </TableCell>

                          <TableCell>
                            {student.daysSinceAssessed !== null ? (
                              <Chip
                                label={
                                  student.daysSinceAssessed === 0
                                    ? en
                                      ? 'Today'
                                      : 'Сегодня'
                                    : en
                                      ? `${student.daysSinceAssessed} d ago`
                                      : `${student.daysSinceAssessed} дн. назад`
                                }
                                size="small"
                                color={daysChipColor(student.daysSinceAssessed)}
                                variant="outlined"
                              />
                            ) : (
                              <Chip
                                label={en ? 'Never' : 'Никогда'}
                                size="small"
                                color="error"
                                variant="outlined"
                              />
                            )}
                          </TableCell>

                          <TableCell>
                            <Chip
                              label={
                                student.scored === false
                                  ? en
                                    ? 'Not assessed'
                                    : 'Не оценён'
                                  : isExcelling
                                    ? en
                                      ? 'Excelling'
                                      : 'Отлично'
                                    : isAttention
                                      ? en
                                        ? 'Needs Help'
                                        : 'Нужна помощь'
                                      : en
                                        ? 'On Track'
                                        : 'На верном пути'
                              }
                              size="small"
                              color={
                                isExcelling
                                  ? 'success'
                                  : isAttention
                                    ? 'error'
                                    : 'default'
                              }
                            />
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}

        {tab === 'themes' && (
          <>
            {/* ══ MASTERY BAR CHART ════════════════════════════════════════════════ */}
            {displayStudents.length > 0 && (
              <Paper
                sx={{
                  p: 4,
                  borderRadius: 4,
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                  mb: 2,
                }}
              >
                <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                  {activeTemplate
                    ? en
                      ? `Mastery % — ${activeTemplate}`
                      : `Усвоение % — ${activeTemplate}`
                    : en
                      ? 'Total Mastered Items by Student'
                      : 'Усвоено элементов по ученикам'}
                </Typography>
                {!activeTemplate && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 1.5 }}
                  >
                    {en
                      ? 'Absolute mastered items count — fair to compare across all levels'
                      : 'Абсолютное количество усвоенных элементов — справедливо для всех уровней'}
                  </Typography>
                )}
                <Box sx={{ height: 280, mt: 1 }}>
                  <Bar
                    data={{
                      labels: displayStudents.map((s) => s.name),
                      datasets: [
                        {
                          label: activeTemplate
                            ? en
                              ? 'Mastery %'
                              : 'Усвоение %'
                            : en
                              ? 'Total Mastered'
                              : 'Усвоено',
                          data: activeTemplate
                            ? displayStudents.map((s) => s.avgMastery)
                            : displayStudents.map((s) => s.totalMastered),
                          backgroundColor: activeTemplate
                            ? displayStudents.map((s) =>
                                s.avgMastery >= 80
                                  ? '#10b981'
                                  : s.avgMastery >= 60
                                    ? '#f59e0b'
                                    : '#ef4444'
                              )
                            : displayStudents.map((s) => {
                                const ratio =
                                  s.totalAssessed > 0
                                    ? (s.totalMastered / s.totalAssessed) * 100
                                    : 0;
                                return ratio >= 80
                                  ? '#10b981'
                                  : ratio >= 60
                                    ? '#f59e0b'
                                    : '#ef4444';
                              }),
                          borderRadius: 8,
                        },
                      ],
                    }}
                    options={{
                      maintainAspectRatio: false,
                      plugins: { legend: { display: false } },
                      scales: {
                        y: {
                          beginAtZero: true,
                          ...(activeTemplate ? { max: 100 } : {}),
                          title: {
                            display: true,
                            text: activeTemplate
                              ? en
                                ? 'Mastery %'
                                : 'Усвоение %'
                              : en
                                ? 'Items mastered'
                                : 'Усвоено эл.',
                          },
                        },
                      },
                    }}
                  />
                </Box>
              </Paper>
            )}

            {/* ══ CLASS THEME HEATMAP ══════════════════════════════════════════════ */}
            <SectionHeader
              icon={<GridViewIcon color="secondary" />}
              title={en ? 'Class Theme Heatmap' : 'Тепловая карта тем'}
              subtitle={
                activeTemplate
                  ? en
                    ? `${activeTemplate} — per-student mastery per theme.  ≥80% green · 60-79% yellow · 40-59% orange · <40% red`
                    : `${activeTemplate} — усвоение по темам для каждого ученика.`
                  : en
                    ? 'Select a template above to see the theme heatmap (themes are only comparable within the same template)'
                    : 'Выберите шаблон выше, чтобы увидеть тепловую карту тем'
              }
            />
            {!activeTemplate ? (
              <Alert
                severity="info"
                variant="outlined"
                sx={{ borderRadius: 3, mb: 3 }}
              >
                <Typography variant="body2">
                  {en
                    ? 'Theme heatmap requires a specific template to be selected — themes differ between templates and cannot be meaningfully compared across levels.'
                    : 'Тепловая карта тем требует выбора конкретного шаблона — темы различаются между шаблонами.'}
                </Typography>
              </Alert>
            ) : activeHeatmap?.studentRows?.length > 0 &&
              activeHeatmap?.themes?.length > 0 ? (
              <TableContainer
                component={Paper}
                sx={{
                  borderRadius: 4,
                  overflow: 'auto',
                  boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                  mb: 3,
                }}
              >
                <Table
                  size="small"
                  sx={{
                    minWidth: Math.max(
                      600,
                      activeHeatmap.themes.length * 90 + 160
                    ),
                  }}
                >
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.100' }}>
                      <TableCell
                        sx={{
                          fontWeight: 700,
                          minWidth: 150,
                          position: 'sticky',
                          left: 0,
                          bgcolor: 'grey.100',
                          zIndex: 2,
                        }}
                      >
                        {en ? 'Student' : 'Ученик'}
                      </TableCell>
                      {activeHeatmap.themes.map((name, i) => (
                        <TableCell
                          key={i}
                          sx={{
                            fontWeight: 700,
                            minWidth: 85,
                            textAlign: 'center',
                          }}
                        >
                          <Tooltip title={name} arrow>
                            <Typography
                              variant="caption"
                              sx={{
                                fontWeight: 700,
                                display: 'block',
                                maxWidth: 82,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {name}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {activeHeatmap.studentRows.map((row, ri) => (
                      <TableRow key={ri} hover>
                        <TableCell
                          sx={{
                            fontWeight: 700,
                            position: 'sticky',
                            left: 0,
                            bgcolor: 'background.paper',
                            zIndex: 1,
                          }}
                        >
                          {row.studentName}
                        </TableCell>
                        {activeHeatmap.themes.map((name, ti) => {
                          const pct = row.themes[name];
                          return (
                            <TableCell
                              key={ti}
                              align="center"
                              sx={{
                                bgcolor: masteryBg(pct),
                                color: masteryFg(pct),
                                fontWeight: 700,
                                fontSize: '0.8rem',
                                transition: 'background 0.2s',
                              }}
                            >
                              {pct !== undefined ? `${pct}%` : '—'}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            ) : (
              <Alert
                severity="warning"
                variant="outlined"
                sx={{ borderRadius: 3, mb: 3 }}
              >
                {en
                  ? `No theme data available for ${activeTemplate}.`
                  : `Нет данных по темам для шаблона ${activeTemplate}.`}
              </Alert>
            )}

            {/* ══ MOST STRUGGLED ITEMS + TEMPLATE PERFORMANCE ═════════════════════ */}
            <Grid container spacing={3} sx={{ mt: 4 }}>
              {/* Most Struggled Items */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    height: '100%',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      mb: 3,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <WarningIcon color="error" />
                    {en ? 'Most Struggled Items' : 'Самые трудные элементы'}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: -2, mb: 2 }}
                  >
                    {en
                      ? 'Score 0 = not known yet. Share of students who were assessed on this word (3+ needed).'
                      : 'Оценка 0 = ещё не знают. Доля учеников, которых оценивали по этому слову (нужно 3+).'}
                  </Typography>
                  {!data.topStruggledItems?.length ? (
                    <Box sx={{ textAlign: 'center', py: 6 }}>
                      <CheckCircleOutlineIcon
                        color="success"
                        sx={{ fontSize: 56, mb: 1 }}
                      />
                      <Typography color="text.secondary">
                        {en
                          ? 'No items scored 0 across your class!'
                          : 'Нет элементов с оценкой 0!'}
                      </Typography>
                    </Box>
                  ) : (
                    <List disablePadding>
                      {data.topStruggledItems.slice(0, 10).map((item, i) => (
                        <ListItem
                          key={i}
                          disableGutters
                          sx={{
                            py: 1.5,
                            borderBottom: '1px solid',
                            borderColor: 'grey.100',
                          }}
                        >
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mb: 0.5,
                                flexWrap: 'wrap',
                              }}
                            >
                              <Typography
                                variant="body1"
                                sx={{ fontWeight: 700 }}
                              >
                                {item.text}
                              </Typography>
                              {item.templateName && (
                                <Chip
                                  label={item.templateName}
                                  size="small"
                                  variant="outlined"
                                />
                              )}
                              <Chip
                                label={item.type}
                                size="small"
                                color={
                                  item.type === 'phrase'
                                    ? 'secondary'
                                    : 'primary'
                                }
                                variant="outlined"
                              />
                            </Box>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              noWrap
                            >
                              {item.themes.join(' · ')}
                            </Typography>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                                mt: 0.75,
                              }}
                            >
                              <LinearProgress
                                variant="determinate"
                                value={
                                  item.percent ??
                                  (data.totalStudents > 0
                                    ? (item.studentCount / data.totalStudents) *
                                      100
                                    : 0)
                                }
                                color="error"
                                sx={{ flex: 1, height: 6, borderRadius: 3 }}
                              />
                              <Typography
                                variant="caption"
                                sx={{
                                  fontWeight: 700,
                                  color: 'error.main',
                                  minWidth: 68,
                                  textAlign: 'right',
                                }}
                              >
                                {item.studentCount}/
                                {item.assessedCount ?? data.totalStudents}{' '}
                                {en ? 'students' : 'уч.'}
                              </Typography>
                            </Box>
                          </Box>
                        </ListItem>
                      ))}
                    </List>
                  )}
                </Paper>
              </Grid>

              {/* Template Performance */}
              <Grid size={{ xs: 12, md: 6 }}>
                <Paper
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    height: '100%',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                  }}
                >
                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      mb: 3,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                    }}
                  >
                    <SchoolIcon color="primary" />
                    {en ? 'Template Performance' : 'Успеваемость по шаблонам'}
                  </Typography>
                  {!Object.keys(data.templateStats || {}).length ? (
                    <Typography
                      color="text.secondary"
                      sx={{ textAlign: 'center', py: 6 }}
                    >
                      {en
                        ? 'No template data yet.'
                        : 'Данных по шаблонам пока нет.'}
                    </Typography>
                  ) : (
                    <>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mb: 1.5 }}
                      >
                        {en
                          ? 'Click a template to filter the view above'
                          : 'Нажмите на шаблон, чтобы отфильтровать таблицу выше'}
                      </Typography>
                      <List disablePadding>
                        {Object.entries(data.templateStats).map(
                          ([name, stats], i) => {
                            const isActive = activeTemplate === name;
                            return (
                              <ListItem
                                key={i}
                                disableGutters
                                onClick={() =>
                                  setActiveTemplate(isActive ? null : name)
                                }
                                sx={{
                                  py: 2,
                                  px: 1.5,
                                  flexDirection: 'column',
                                  alignItems: 'flex-start',
                                  borderRadius: 2,
                                  cursor: 'pointer',
                                  bgcolor: isActive
                                    ? 'primary.50'
                                    : 'transparent',
                                  outline: isActive
                                    ? '2px solid'
                                    : '2px solid transparent',
                                  outlineColor: isActive
                                    ? 'primary.main'
                                    : 'transparent',
                                  borderBottom: '1px solid',
                                  borderColor: 'grey.100',
                                  transition: 'all 0.15s ease',
                                  '&:hover': { bgcolor: 'grey.50' },
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    width: '100%',
                                    mb: 1,
                                  }}
                                >
                                  <Typography
                                    variant="subtitle2"
                                    sx={{
                                      fontWeight: 700,
                                      color: isActive
                                        ? 'primary.main'
                                        : 'text.primary',
                                    }}
                                  >
                                    {name}
                                  </Typography>
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      alignItems: 'center',
                                      gap: 1,
                                    }}
                                  >
                                    <Chip
                                      label={`${stats.avgMastery}%`}
                                      size="small"
                                      color={masteryChipColor(stats.avgMastery)}
                                    />
                                    <Typography
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {stats.totalProfiles}{' '}
                                      {en ? 'used' : 'оценок'}
                                    </Typography>
                                  </Box>
                                </Box>
                                <LinearProgress
                                  variant="determinate"
                                  value={stats.avgMastery}
                                  color={masteryChipColor(stats.avgMastery)}
                                  sx={{
                                    width: '100%',
                                    height: 8,
                                    borderRadius: 4,
                                  }}
                                />
                              </ListItem>
                            );
                          }
                        )}
                      </List>
                    </>
                  )}
                </Paper>
              </Grid>
            </Grid>
          </>
        )}

        {tab === 'activity' && (
          <>
            {/* ══ MONTHLY ASSESSMENT ACTIVITY ══════════════════════════════════════ */}
            {data.monthlyActivity?.length > 0 && (
              <>
                <SectionHeader
                  icon={<HistoryIcon color="primary" />}
                  title={
                    en
                      ? 'Assessment Activity — Last 12 Months'
                      : 'Активность — последние 12 месяцев'
                  }
                  subtitle={
                    en
                      ? 'Bars = assessments started · second series = average change on the same units between a reassessment and the previous assessment (percentage points)'
                      : 'Столбцы = начатых оценок · вторая серия = среднее изменение по тем же темам между переоценкой и прошлой оценкой (п.п.)'
                  }
                />
                <Paper
                  sx={{
                    p: 4,
                    borderRadius: 4,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                  }}
                >
                  <Box sx={{ height: 300 }}>
                    <Bar
                      data={{
                        labels: data.monthlyActivity.map((m) => m.month),
                        datasets: [
                          {
                            label: en ? 'Assessments' : 'Оценок',
                            data: data.monthlyActivity.map((m) => m.count),
                            backgroundColor: theme.palette.primary.main + 'cc',
                            borderRadius: 6,
                            yAxisID: 'y',
                          },
                          {
                            label: en
                              ? 'Avg growth on reassessment (pp)'
                              : 'Ср. рост при переоценке (п.п.)',
                            data: data.monthlyActivity.map((m) => m.avgGain),
                            backgroundColor: theme.palette.success.light + '55',
                            borderColor: theme.palette.success.main,
                            borderWidth: 2,
                            borderRadius: 6,
                            yAxisID: 'y1',
                          },
                        ],
                      }}
                      options={{
                        maintainAspectRatio: false,
                        plugins: { legend: { display: true, position: 'top' } },
                        scales: {
                          y: {
                            beginAtZero: true,
                            title: {
                              display: true,
                              text: en ? 'Count' : 'Кол-во',
                            },
                            position: 'left',
                          },
                          y1: {
                            suggestedMin: -10,
                            suggestedMax: 30,
                            position: 'right',
                            title: { display: true, text: en ? 'pp' : 'п.п.' },
                            grid: { drawOnChartArea: false },
                          },
                        },
                      }}
                    />
                  </Box>
                </Paper>
              </>
            )}
          </>
        )}

        {tab === 'school' && userRole === 'admin' && (
          <>
            {/* ══ ADMIN: GLOBAL SCHOOL OVERVIEW ════════════════════════════════════ */}
            {globalData && (
              <Box sx={{ mt: 8, mb: 4 }}>
                <Typography
                  variant="h4"
                  sx={{ fontWeight: 800, mb: 1, color: 'primary.dark' }}
                >
                  {en ? 'Global School Overview' : 'Общая картина школы'}
                </Typography>
                <Divider sx={{ mb: 4 }} />

                {/* Global summary cards */}
                <Grid container spacing={3} sx={{ mb: 4 }}>
                  {[
                    {
                      label: en ? 'Total Students' : 'Всего учеников',
                      value: globalData.summary.totalStudents,
                      color: '#3b82f6',
                      icon: <PeopleIcon />,
                    },
                    {
                      label: en ? 'Total Teachers' : 'Учителей',
                      value: globalData.summary.totalTeachers,
                      color: '#7c3aed',
                      icon: <SchoolIcon />,
                    },
                    {
                      label: en ? 'Total Assessments' : 'Всего оценок',
                      value: globalData.summary.totalAssessments,
                      color: '#10b981',
                      icon: <AssessmentIcon />,
                    },
                    {
                      label: en ? 'Global Avg Mastery' : 'Ср. усвоение',
                      value: `${globalData.summary.globalAverageMastery}%`,
                      color:
                        globalData.summary.globalAverageMastery >= 70
                          ? '#10b981'
                          : '#f59e0b',
                      icon: <TrendingUpIcon />,
                    },
                  ].map((s, i) => (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }} key={i}>
                      <Card sx={{ borderRadius: 4 }}>
                        <CardContent
                          sx={{ display: 'flex', alignItems: 'center', p: 3 }}
                        >
                          <Avatar
                            sx={{
                              bgcolor: s.color,
                              width: 48,
                              height: 48,
                              mr: 2,
                            }}
                          >
                            {s.icon}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {s.label}
                            </Typography>
                            <Typography variant="h4" sx={{ fontWeight: 700 }}>
                              {s.value}
                            </Typography>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  ))}
                </Grid>

                <Grid container spacing={3}>
                  {/* Teacher performance bar chart */}
                  <Grid size={{ xs: 12, md: 7 }}>
                    <Paper sx={{ p: 4, borderRadius: 4, height: '100%' }}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          fontWeight: 700,
                        }}
                      >
                        <SchoolIcon color="primary" />{' '}
                        {en ? 'Teacher Performance' : 'Успеваемость учителей'}
                      </Typography>
                      <Box sx={{ height: 300, mt: 2 }}>
                        <Bar
                          data={{
                            labels: globalData.teacherStats.map(
                              (t) =>
                                `${t.teacherName} (${t.assessedStudents ?? 0}/${t.studentCount})`
                            ),
                            datasets: [
                              {
                                label: en
                                  ? 'Mastery % (assessed students, own level)'
                                  : 'Освоение % (оценённые ученики, свой уровень)',
                                data: globalData.teacherStats.map((t) =>
                                  t.hasData === false ? null : t.averageMastery
                                ),
                                backgroundColor: globalData.teacherStats.map(
                                  (t) =>
                                    t.averageMastery >= 80
                                      ? '#10b981'
                                      : t.averageMastery >= 60
                                        ? '#f59e0b'
                                        : '#ef4444'
                                ),
                                borderRadius: 6,
                              },
                            ],
                          }}
                          options={{
                            maintainAspectRatio: false,
                            plugins: { legend: { display: false } },
                            scales: {
                              y: {
                                beginAtZero: true,
                                max: 100,
                                title: { display: true, text: '%' },
                              },
                            },
                          }}
                        />
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Most difficult themes */}
                  <Grid size={{ xs: 12, md: 5 }}>
                    <Paper sx={{ p: 4, borderRadius: 4, height: '100%' }}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          fontWeight: 700,
                        }}
                      >
                        <WarningIcon color="error" />{' '}
                        {en ? 'Hardest Themes (School-wide)' : 'Сложные темы'}
                      </Typography>
                      <List disablePadding>
                        {globalData.difficultThemes.slice(0, 7).map((t, i) => (
                          <ListItem
                            key={i}
                            disableGutters
                            sx={{
                              py: 1.5,
                              borderBottom: '1px solid',
                              borderColor: 'grey.100',
                            }}
                          >
                            <Box sx={{ flex: 1 }}>
                              <Box
                                sx={{
                                  display: 'flex',
                                  justifyContent: 'space-between',
                                  mb: 0.5,
                                }}
                              >
                                <Typography
                                  variant="body2"
                                  sx={{ fontWeight: 600 }}
                                >
                                  {t.name}
                                  {t.students ? (
                                    <Typography
                                      component="span"
                                      variant="caption"
                                      color="text.secondary"
                                    >
                                      {' '}
                                      · {t.students} {en ? 'students' : 'уч.'}
                                    </Typography>
                                  ) : null}
                                </Typography>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700,
                                    color: masteryFg(t.masteryRate),
                                  }}
                                >
                                  {t.masteryRate}%
                                </Typography>
                              </Box>
                              <LinearProgress
                                variant="determinate"
                                value={t.masteryRate}
                                color={masteryChipColor(t.masteryRate)}
                                sx={{ height: 6, borderRadius: 3 }}
                              />
                            </Box>
                          </ListItem>
                        ))}
                      </List>
                    </Paper>
                  </Grid>

                  {/* Monthly growth trend as chart */}
                  <Grid size={{ xs: 12 }}>
                    <Paper sx={{ p: 4, borderRadius: 4 }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, mb: 3 }}>
                        {en
                          ? 'Growth on reassessment, by month'
                          : 'Рост при переоценке по месяцам'}
                      </Typography>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: -2, mb: 2 }}
                      >
                        {en
                          ? 'Average change (percentage points) on the same units between a reassessment and the previous assessment of that level. n = number of reassessments.'
                          : 'Среднее изменение (п.п.) по тем же темам между переоценкой и прошлой оценкой того же уровня. n — число переоценок.'}
                      </Typography>
                      {globalData.growthTrends.some(
                        (t) => t.avgGain != null
                      ) ? (
                        <Box sx={{ height: 260 }}>
                          <Bar
                            data={{
                              labels: globalData.growthTrends.map(
                                (t) => `${t.month} (n=${t.gainSample})`
                              ),
                              datasets: [
                                {
                                  label: en
                                    ? 'Avg growth (pp)'
                                    : 'Ср. рост (п.п.)',
                                  data: globalData.growthTrends.map(
                                    (t) => t.avgGain
                                  ),
                                  backgroundColor: globalData.growthTrends.map(
                                    (t) =>
                                      (t.avgGain ?? 0) >= 0
                                        ? '#10b98188'
                                        : '#ef444488'
                                  ),
                                  borderColor: globalData.growthTrends.map(
                                    (t) =>
                                      (t.avgGain ?? 0) >= 0
                                        ? '#10b981'
                                        : '#ef4444'
                                  ),
                                  borderWidth: 2,
                                  borderRadius: 6,
                                },
                              ],
                            }}
                            options={{
                              maintainAspectRatio: false,
                              plugins: { legend: { display: false } },
                              scales: {
                                y: {
                                  suggestedMin: -10,
                                  suggestedMax: 30,
                                  title: {
                                    display: true,
                                    text: en ? 'pp' : 'п.п.',
                                  },
                                },
                              },
                            }}
                          />
                        </Box>
                      ) : (
                        <Box sx={{ textAlign: 'center', py: 4 }}>
                          <Typography color="text.secondary">
                            {en
                              ? 'No reassessments with comparable units yet'
                              : 'Пока нет переоценок с сопоставимыми темами'}
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Grid>

                  {/* Assessment type pie */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Paper sx={{ p: 4, borderRadius: 4, height: '100%' }}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          fontWeight: 700,
                        }}
                      >
                        <AssessmentIcon color="primary" />{' '}
                        {en ? 'Assessment Types' : 'Типы оценок'}
                      </Typography>
                      <Box sx={{ height: 250, mt: 2 }}>
                        <Pie
                          data={{
                            labels: [
                              en ? 'Initial' : 'Начальная',
                              en ? 'Mid-term' : 'Промежуточная',
                              en ? 'Final' : 'Финальная',
                            ],
                            datasets: [
                              {
                                data: [
                                  globalData.typeDistribution.initial,
                                  globalData.typeDistribution['mid-term'],
                                  globalData.typeDistribution.final,
                                ],
                                backgroundColor: [
                                  '#E05570',
                                  '#f59e0b',
                                  '#10b981',
                                ],
                              },
                            ],
                          }}
                        />
                      </Box>
                    </Paper>
                  </Grid>

                  {/* Inactive students */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Paper sx={{ p: 4, borderRadius: 4, height: '100%' }}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          fontWeight: 700,
                        }}
                      >
                        <HistoryIcon color="error" />{' '}
                        {en
                          ? 'Not assessed for 30+ days'
                          : 'Без оценки 30+ дней'}
                      </Typography>
                      <List dense sx={{ maxHeight: 250, overflow: 'auto' }}>
                        {globalData.inactiveStudents.length === 0 ? (
                          <Box sx={{ textAlign: 'center', py: 4 }}>
                            <CheckCircleOutlineIcon
                              color="success"
                              sx={{ fontSize: 40, mb: 1 }}
                            />
                            <Typography variant="body2" color="text.secondary">
                              {en
                                ? 'All students active!'
                                : 'Все ученики активны!'}
                            </Typography>
                          </Box>
                        ) : (
                          globalData.inactiveStudents.map((s, i) => (
                            <ListItem key={i} divider>
                              <ListItemText
                                primary={s.name}
                                secondary={`${en ? 'Teacher' : 'Учитель'}: ${s.teacher} · ${
                                  s.daysSinceAssessed == null
                                    ? en
                                      ? 'never assessed'
                                      : 'не оценивался'
                                    : en
                                      ? `${s.daysSinceAssessed} d ago`
                                      : `${s.daysSinceAssessed} дн. назад`
                                }`}
                              />
                            </ListItem>
                          ))
                        )}
                      </List>
                    </Paper>
                  </Grid>

                  {/* Parent collaboration */}
                  <Grid size={{ xs: 12, md: 4 }}>
                    <Paper sx={{ p: 4, borderRadius: 4, height: '100%' }}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          fontWeight: 700,
                        }}
                      >
                        <ChatIcon color="secondary" />{' '}
                        {en
                          ? 'Parent Collaboration'
                          : 'Вовлечённость родителей'}
                      </Typography>
                      <Box sx={{ textAlign: 'center', mt: 3 }}>
                        <Typography
                          variant="h2"
                          sx={{ fontWeight: 900, color: 'secondary.main' }}
                        >
                          {globalData.summary.totalParentContributions}
                        </Typography>
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 700, mb: 3 }}
                        >
                          {en ? 'TOTAL INTERACTIONS' : 'ВСЕГО ВЗАИМОДЕЙСТВИЙ'}
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-around',
                          }}
                        >
                          <Box>
                            <Typography variant="h5">
                              {globalData.collaborativeStats.totalComments}
                            </Typography>
                            <Typography variant="caption">
                              {en ? 'COMMENTS' : 'КОММЕНТАРИИ'}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="h5">
                              {globalData.collaborativeStats.totalObservations}
                            </Typography>
                            <Typography variant="caption">
                              {en ? 'OBSERVATIONS' : 'НАБЛЮДЕНИЯ'}
                            </Typography>
                          </Box>
                        </Box>
                      </Box>
                    </Paper>
                  </Grid>
                </Grid>
              </Box>
            )}
          </>
        )}
        <PdfExportPanel
          open={pdfPanelOpen}
          onClose={() => setPdfPanelOpen(false)}
          language={language}
          initialStudentIds={exportIds}
          defaultMode={exportIds.length ? 'combined' : 'group'}
        />
      </Container>
    </Layout>
  );
}
